// @ts-nocheck
import { useForm } from 'react-hook-form';
import { Info, FolderOpen } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getPriorityText, getStatusText } from '@/helpers/enumStylers';
import { FormSelect } from '@/components/shared/FormSelect';
import { DispatchOptions } from './DispatchOptions';
import { CustomFieldDisplay } from '../../../../components/shared/CustomFieldDisplay';
import { useSkills } from '@/hooks/api/useSkills';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCreateBulkTickets, useCreateTicket } from '@/hooks/api/useTickets';
import { Project } from '@/types';
import { toast } from 'sonner';
import { useProjects } from '@/hooks/api/useProjects';
import { FormInput } from '@/components/shared/FormInput';
import { useDialog } from '@/providers/DialogProvider';
import { Asset } from '@/types/asset';
import { LinkedAssetsSection } from './AssetLinkSection';
import { CreateTicketDto, CreateTicketSchema } from '@packages/database/dtos';
import { WizardForm } from '@/components/WizardForm';
import { SummaryCard } from '@/components/shared/SummaryCard';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { FormTextarea } from '@/components/shared/FormTextArea';
import { useDialogManager } from '@/hooks/useDialogManager';
import { zodResolver } from '@hookform/resolvers/zod';
import { TicketPriority, TicketStatus } from '@packages/database/types';
import { TicketStatusDropdown } from '@/components/shared/TicketStatusDropdowm';

export const CreateTicketForm = () => {
  const [autoDispatch, setAutoDispatch] = useState(true);
  const [multiTicket, setMultiTicket] = useState(false);
  const { closeDialog, projectId: preselectedProjectId } = useDialogManager();
  const { setIsDirty } = useDialog();
  const [verifiedAddress, setVerifiedAddress] = useState(false);

  // Load data for dropdowns
  const { projects, isLoading, error: projectsLoadError } = useProjects();
  const {
    data: skills,
    isLoading: areSkillsLoading,
    error: skillsLoadError,
  } = useSkills();

  const methods = useForm<CreateTicketDto>({
    resolver: zodResolver(CreateTicketSchema),
    defaultValues: {
      linkingMode: 'single',
      isAuto: true,
      projectId: preselectedProjectId || undefined,
    },
  });

  const {
    reset,
    setValue,
    watch,
    register,
    formState: { isDirty, errors },
  } = methods;

  const resetFormAndClose = useCallback(() => {
    reset();
    closeDialog();
  }, [reset, closeDialog]);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  // Watch form values
  const verifyLon = watch('lon');
  const verifyLat = watch('lat');
  const verifyAddress = watch('address1');
  const projectId = watch('projectId');
  const linkingMode = watch('linkingMode');
  const linkedItems = watch('linkedItems');

  // Reset custom fields if project type changes
  useEffect(() => {
    setValue('customFields', null);
    // setValue('linkingMode', ''); //TODO: handle resetting mode depending on asset type changes
  }, [projectId, setValue]);

  // Auto-clear errors when user edits invalid fields
  useEffect(() => {
    const subscription = methods.watch((_, { name }) => {
      if (!name) return;
      methods.clearErrors(name);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  // Get selected project details
  const {
    selectedProjectFields,
    selectedProjectAssets,
    selectedProjectPremises,
    selectedProjectTypeLayers,
    selectedProject,
  } = useMemo(() => {
    // Selected project
    const selectedProject = projects.find(
      (project) => project.id === projectId,
    );

    // Selected project type layer filters
    const selectedProjectTypeLayers = Array.from(
      new Map(
        (selectedProject?.projectType?.layers ?? []).map((l) => [
          l.layer.id,
          l.layer,
        ]),
      ).values(),
    );

    return {
      selectedProject,
      selectedProjectFields:
        selectedProject?.projectType?.customFieldDefinitions,
      selectedProjectAssets: selectedProject?.assets,
      selectedProjectPremises: selectedProject?.premises,
      selectedProjectTypeLayers: selectedProjectTypeLayers,
    };
  }, [projectId, projects]);

  useEffect(() => {
    verifyLon && verifyLat && verifyAddress
      ? setVerifiedAddress(true)
      : setVerifiedAddress(false);
  }, [verifyLon, verifyLat, verifyAddress]);

  const { createTicket, isCreating } = useCreateTicket();
  const { createBulkTickets, isCreating: bulkTicketLoading } =
    useCreateBulkTickets();

  const onSubmit = (data: CreateTicketDto) => {
    const { linkedItems = [], ...ticketData } = data;

    // Separate the IDs based on type
    const assetIds = linkedItems
      ?.filter((item) => item.type === 'asset')
      .map((item) => item.id);

    const premiseIds = linkedItems
      ?.filter((item) => item.type === 'premise')
      .map((item) => item.id);

    const totalSelected = linkedItems?.length;

    // -------------------------------------------------------
    // BULK CREATION (Multiple Items)
    // -------------------------------------------------------
    if (totalSelected && totalSelected > 1) {
      createBulkTickets(
        {
          commonData: { projectId: projectId },
          ticketData: { ...ticketData },
          assetIds: assetIds,
          premiseIds: premiseIds,
          isAuto: autoDispatch,
        },
        {
          onSuccess() {
            toast.success(`Successfully created ${totalSelected} new tickets`);
            resetFormAndClose();
          },
          onError(err) {
            // Only close form if auto-dispatch error occurs since ticket is still created successfully
            //@ts-ignore
            if (err.response?.data?.error?.code === 'Auto-Dispatch') {
              resetFormAndClose();
            }
          },
        },
      );
    }
    // -------------------------------------------------------
    // SINGLE CREATION
    // -------------------------------------------------------
    else if (totalSelected === 1) {
      const singleItem = linkedItems[0];

      createTicket(
        {
          ...ticketData,
          projectId: projectId,
          isAuto: autoDispatch,
          linkedItems,
          assetId: singleItem.type === 'asset' ? singleItem.id : undefined,
          premiseId: singleItem.type === 'premise' ? singleItem.id : undefined,
        },
        {
          onSuccess() {
            toast.success(`Successfully created ticket`);
            resetFormAndClose();
          },
          onError(err) {
            // Only close form if auto-dispatch error occurs since ticket is still created successfully
            //@ts-ignore
            if (err.response?.data?.error?.code === 'Auto-Dispatch') {
              resetFormAndClose();
            }
          },
        },
      );
    }
    // -------------------------------------------------------
    // AD-HOC (No Linked Items)
    // -------------------------------------------------------
    else {
      createTicket(
        {
          ...ticketData,
          projectId: projectId,
          isAuto: autoDispatch,
          // assetId: undefined,
          // premiseId: undefined,
        },
        {
          onSuccess() {
            toast.success(`Successfully created ad-hoc ticket`);
            resetFormAndClose();
          },
          onError(err) {
            // Only close form if auto-dispatch error occurs since ticket is still created successfully
            //@ts-ignore
            if (err.response?.data?.error?.code === 'Auto-Dispatch') {
              resetFormAndClose();
            }
          },
        },
      );
    }
  };

  // Step validation functions
  const validateBasicInfo = async () => {
    const isValid = await methods.trigger([
      'projectId',
      'priority',
      'status',
      'startDate',
    ]);
    if (!isValid) {
      toast.error('Please fill in all required fields');
    }
    return isValid;
  };

  const validateAssetLinks = async () => {
    // if (multiTicket) return true;

    // 'Ad-hoc' tickets must have a valid address
    if (linkingMode === 'adhoc') {
      // Check if lon/lat info is present
      if (!verifiedAddress) {
        toast.error(
          'Please select a verified address from the search results.',
        );
        return false;
      }
    }
    if (linkingMode === 'single') {
      if (!linkedItems || linkedItems.length !== 1) {
        toast.error('Must select one asset in this mode');
        return false;
      }
    }
    if (linkingMode === 'multi') {
      if (!linkedItems || linkedItems.length < 1) {
        toast.error('Must select 2 or more assets in this mode');
        return false;
      }
    }

    return true;
  };

  // Define wizard steps
  const wizardSteps = [
    {
      id: 'basic-info',
      title: 'Basic Info',
      description: 'Configure the ticket basics',
      validate: validateBasicInfo,
      content: (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Project */}
            {projectsLoadError ? (
              <div className="text-red-500 col-span-2">
                Failed to load projects
              </div>
            ) : (
              <FormSelect
                name="projectId"
                label="Project"
                rules={{ required: 'Project is required' }}
                disabled={isLoading}
                placeholder={
                  isLoading ? 'Loading Projects...' : 'Select Project'
                }
                options={
                  projects?.map((s) => ({
                    value: s.id,
                    label: s.name,
                  })) ?? []
                }
              />
            )}

            {/* Priority */}
            <FormSelect
              name="priority"
              label="Priority"
              rules={{ required: 'Priority is required' }}
              options={Object.values(TicketPriority).map((p) => ({
                value: p,
                label: getPriorityText(p),
              }))}
            />

            {/* Status */}
            <TicketStatusDropdown required />

            {/* Skills */}
            {skillsLoadError ? (
              <div className="text-red-500 col-span-2">
                Failed to load skills
              </div>
            ) : (
              <FormSelect
                name="skillsRequired"
                label="Skills Required"
                disabled={areSkillsLoading}
                placeholder={
                  areSkillsLoading ? 'Loading Skills...' : 'Select Skill(s)'
                }
                multiple
                options={
                  skills?.map((s) => ({
                    value: s.id,
                    label: s.name,
                  })) ?? []
                }
              />
            )}

            {/* Start Date */}
            <FormInput
              name="startDate"
              type="date"
              label="Start Date"
              rules={{ required: 'Start date is required' }}
            />

            {/* Description */}
            <div className="col-span-2 flex flex-col space-y-2">
              <FormTextarea
                name="description"
                label="Description"
                placeholder="Describe the issue or outage..."
                rows={5}
                // rules={{ required: 'Description is required' }}
              />
            </div>

            {selectedProjectFields && selectedProjectFields.length ? (
              <div>
                {selectedProjectFields.map(
                  (field) =>
                    field.isVisible && (
                      <div key={field.id} className="col-span-2 sm:col-span-1">
                        <CustomFieldDisplay
                          field={field}
                          name="customFields"
                          isCreateMode={true}
                        />
                      </div>
                    ),
                )}
              </div>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: 'asset-link',
      title: 'Asset Linking',
      description: 'Link project level assets',
      validate: validateAssetLinks,
      content: (
        <div className="space-y-6">
          <div className="rounded-lg border bg-muted/10 p-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
              <FolderOpen className="h-4 w-4" />
              Selected Project
            </div>

            <div className="space-y-2.5 text-sm">
              {/* Project Name & Number */}
              <div className="grid grid-cols-[100px_1fr] items-baseline gap-2">
                <span className="text-muted-foreground font-medium">Name:</span>
                <div>
                  <span className="font-medium text-foreground mr-2">
                    {selectedProject?.name || 'Untitled Project'}
                  </span>
                </div>
              </div>

              {/* Project Type */}
              <div className="grid grid-cols-[100px_1fr] items-baseline gap-2">
                <span className="text-muted-foreground font-medium">
                  Project Type:
                </span>
                <span className="text-foreground">
                  {selectedProject?.projectType?.name || 'General'}
                </span>
              </div>

              {/* Asset Badges */}
              <div className="grid grid-cols-[100px_1fr] items-start gap-2">
                <span className="text-muted-foreground font-medium py-0.5">
                  Asset Types:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedProjectTypeLayers &&
                  selectedProjectTypeLayers.length > 0 ? (
                    selectedProjectTypeLayers.map((type) => (
                      <Badge
                        key={type.id}
                        variant="outline"
                        className="bg-background text-[10px] h-5 px-2 font-medium border-slate-300"
                      >
                        {type.key === 'PREMISE' ? 'Premises' : type.key}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground italic text-xs py-0.5">
                      None configured
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="pt-4 border-t">
            <LinkedAssetsSection
              selectedProjectAssets={selectedProjectAssets}
              selectedProjectPremises={selectedProjectPremises}
              projectTypeLayers={selectedProjectTypeLayers}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'dispatch',
      title: 'Dispatch',
      description: 'When enabled, tickets will be auto-dispatched to crews',
      content: (
        <div className="space-y-6">
          {!multiTicket ? (
            <>
              <DispatchOptions
                autoDispatch={autoDispatch}
                onAutoDispatchChange={setAutoDispatch}
              />
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Info className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Auto-dispatch is not available for multi-ticket creation</p>
              <p className="text-sm mt-2">
                Each ticket will need to be assigned manually
              </p>
            </div>
          )}
        </div>
      ),
    },

    {
      id: 'summary',
      title: 'Review',
      description: 'Review and confirm your ticket details',
      content: (
        <SummaryCard
          title="Ticket Summary"
          sections={[
            {
              items: [
                {
                  label: 'Project Name',
                  value:
                    projects.find((p) => p.id === watch('projectId'))?.name ||
                    'N/A',
                },
                {
                  label: 'Create Type',
                  value:
                    linkingMode === 'adhoc'
                      ? 'Ad-hoc Ticket'
                      : linkingMode === 'multi'
                        ? `Multi-Ticket (${linkedItems?.length})`
                        : 'Single Ticket',
                },
                // {
                //   label: 'Location',
                //   value:
                //     linkingMode === 'adhoc' && verifiedAddress
                //       ? verifiedAddress
                //       : 'N/A',
                //   muted: linkingMode !== 'adhoc',
                // },
                {
                  label: 'Dispatch Mode',
                  value: autoDispatch ? 'Auto-Dispatch' : 'Manual Dispatch',
                },
                // {
                //   label: 'Crew Assignment',
                //   // If auto dispatch then show crew name else N/A
                //   value: autoDispatch,
                // },
                {
                  label: 'Status',
                  value: getStatusText(watch('status')),
                },
                {
                  label: 'Priority',
                  value: getPriorityText(watch('priority')),
                },
                {
                  label: 'Start Date',
                  value: watch('startDate')
                    ? new Date(watch('startDate')).toLocaleDateString()
                    : 'N/A',
                },
                linkingMode !== 'adhoc'
                  ? {
                      label: 'Linked Assets',
                      value: linkedItems
                        ? `${linkedItems.length} item(s) linked`
                        : '0 items',
                    }
                  : {
                      label: 'Address',
                      value: watch('address1') || 'N/A',
                    },
                {
                  label: 'Description',
                  value: watch('description') || 'N/A',
                },
              ],
            },
          ]}
        />
      ),
    },
  ];

  return (
    <WizardForm
      methods={methods}
      onSubmit={onSubmit}
      steps={wizardSteps}
      submitText="Create"
      cancelText="Cancel"
      onCancel={resetFormAndClose}
      isSubmitting={isCreating || bulkTicketLoading}
      showStepNumbers={true}
      useUrlParams={true}
      urlParamName="step"
      canResume={!!projectId}
    />
  );
};
